import React, { createContext, useContext, useReducer } from "react";

// Initial state for the representative application
const initialState = {
  user: JSON.parse(localStorage.getItem("representativeUser")) || JSON.parse(localStorage.getItem("repUser")) || null,
  selectedProgramme: JSON.parse(localStorage.getItem("selectedProgramme")) || null,
  programmes: [],
  scores: {
    academicContent: 0,
    staffing: 0,
    physicalFacilities: 0,
    library: 0,
    funding: 0,
    researchCollaboration: 0,
    tracerEmployer: 0,
  },
  totalScore: 0,
  recommendation: null,
  submissionResults: null, // Store submission results for the results page
  isDraft: true,
  isLoading: false,
  error: null,
};

// Action types
export const ACTION_TYPES = {
  SET_USER: "SET_USER",
  SET_SELECTED_PROGRAMME: "SET_SELECTED_PROGRAMME",
  SET_PROGRAMMES: "SET_PROGRAMMES",
  UPDATE_SCORES: "UPDATE_SCORES",
  SET_CALCULATION: "SET_CALCULATION",
  SET_SUBMISSION_RESULTS: "SET_SUBMISSION_RESULTS",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_STATE: "CLEAR_STATE",
  SET_DRAFT_STATUS: "SET_DRAFT_STATUS",
};

// Reducer function
const contextReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_USER:
      return {
        ...state,
        user: action.payload,
        programmes: action.payload?.programmes || [],
      };
    case ACTION_TYPES.SET_SELECTED_PROGRAMME:
      // Update localStorage when programme is selected
      if (action.payload) {
        localStorage.setItem("selectedProgramme", JSON.stringify(action.payload));
      } else {
        localStorage.removeItem("selectedProgramme");
      }
      return {
        ...state,
        selectedProgramme: action.payload,
        // Reset scores when programme changes
        scores: initialState.scores,
        totalScore: 0,
        recommendation: null,
        isDraft: true,
      };
    case ACTION_TYPES.SET_PROGRAMMES:
      return {
        ...state,
        programmes: action.payload,
      };
    case ACTION_TYPES.UPDATE_SCORES:
      // Update individual score field
      const newScores = {
        ...state.scores,
        [action.payload.field]: action.payload.value,
      };
      // Calculate total
      const total = Object.values(newScores).reduce((sum, score) => sum + Number(score || 0), 0);
      return {
        ...state,
        scores: newScores,
        totalScore: total,
        isDraft: true, // Mark as draft when scores are updated
      };
    case ACTION_TYPES.SET_CALCULATION:
      return {
        ...state,
        totalScore: action.payload.totalScore,
        recommendation: action.payload.recommendation,
      };
    case ACTION_TYPES.SET_SUBMISSION_RESULTS:
      return {
        ...state,
        submissionResults: action.payload,
      };
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case ACTION_TYPES.SET_DRAFT_STATUS:
      return {
        ...state,
        isDraft: action.payload,
      };
    case ACTION_TYPES.CLEAR_STATE:
      localStorage.removeItem("repUser");
      localStorage.removeItem("selectedProgramme");
      return {
        ...initialState,
        user: null,
        selectedProgramme: null,
      };
    default:
      return state;
  }
};

// Create context
const ContextAPI = createContext();

// Context provider component
export const ContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(contextReducer, initialState);

  // Action creators
  const actions = {
    setUser: (user) => {
      dispatch({ type: ACTION_TYPES.SET_USER, payload: user });
      if (user) {
        localStorage.setItem("representativeUser", JSON.stringify(user));
        // Also store with old key for backward compatibility
        localStorage.setItem("repUser", JSON.stringify(user));
        // Dispatch custom event for same-tab changes
        window.dispatchEvent(new Event('repUserDataUpdated'));
      }
    },
    setSelectedProgramme: (programme) => {
      dispatch({ type: ACTION_TYPES.SET_SELECTED_PROGRAMME, payload: programme });
    },
    setProgrammes: (programmes) => {
      dispatch({ type: ACTION_TYPES.SET_PROGRAMMES, payload: programmes });
    },
    updateScores: (field, value) => {
      dispatch({ type: ACTION_TYPES.UPDATE_SCORES, payload: { field, value } });
    },
    setCalculation: (totalScore, recommendation) => {
      dispatch({ type: ACTION_TYPES.SET_CALCULATION, payload: { totalScore, recommendation } });
    },
    setSubmissionResults: (results) => {
      dispatch({ type: ACTION_TYPES.SET_SUBMISSION_RESULTS, payload: results });
    },
    setLoading: (loading) => {
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loading });
    },
    setError: (error) => {
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
    },
    setDraftStatus: (isDraft) => {
      dispatch({ type: ACTION_TYPES.SET_DRAFT_STATUS, payload: isDraft });
    },
    clearState: () => {
      localStorage.removeItem("representativeUser");
      localStorage.removeItem("repUser");
      localStorage.removeItem("selectedProgramme");
      dispatch({ type: ACTION_TYPES.CLEAR_STATE });
    },
  };

  // Combined value to provide
  const value = {
    ...state,
    ...actions,
  };

  return <ContextAPI.Provider value={value}>{children}</ContextAPI.Provider>;
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(ContextAPI);
  if (!context) {
    throw new Error("useAppContext must be used within a ContextProvider");
  }
  return context;
};

// Helper functions
export const getProgrammeById = (programmes, id) => {
  return programmes?.find(prog => prog.id === id || prog.programmeId === id);
};

export const getCurrentProgramme = (context) => {
  return context.selectedProgramme || context.programmes?.[0] || null;
};

export const hasMultipleProgrammes = (context) => {
  return context.programmes?.length > 1;
};

// Score calculation helpers
export const calculateTotalScore = (scores) => {
  return Object.values(scores).reduce((sum, score) => sum + Number(score || 0), 0);
};

export const getRecommendation = (totalScore) => {
  if (totalScore >= 70) return "Full Accreditation";
  if (totalScore >= 50) return "Interim Accreditation";
  if (totalScore >= 40) return "Denied (Reapply within 2 years)";
  return "Denied (Reapply within 5 years)";
};

export const getRecommendationColor = (recommendation) => {
  if (!recommendation) return "gray";
  if (recommendation.includes("Full")) return "green";
  if (recommendation.includes("Interim")) return "yellow";
  return "red";
};

export default ContextAPI;